/**
 * MemberUploadsPanel — real file uploads (photos + documents) tagged to a
 * member, backed by the family_contributions archive. Any uploaded photo
 * can be promoted to the member's profile picture via onSetProfile.
 */
import { useCallback, useEffect, useState } from "react";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Images, FileText, MessageSquare, Star, ExternalLink } from "lucide-react";
import MemberContributeModal from "./MemberContributeModal";

const Btn = ({ onClick, icon: Icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold hover:shadow-sm transition"
    style={{ borderColor: "var(--wofy-border)", background: "#fff" }}
  >
    <Icon className="w-3.5 h-3.5" style={{ color: "#B25D38" }} /> {label}
  </button>
);

export default function MemberUploadsPanel({ treeId, memberId, memberName, currentPhoto, onSetProfile }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    if (!treeId) return;
    http.get(`/family-trees/${treeId}/contributions`, { params: { member_id: memberId, limit: 100 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, [treeId, memberId]);
  useEffect(load, [load]);

  const photos = items.filter((i) => i.kind === "photo" && i.url);
  const docs = items.filter((i) => i.kind === "document" && i.url);

  return (
    <div data-testid="member-uploads-panel">
      <div className="flex flex-wrap gap-2 mb-3">
        <Btn onClick={() => setModal("photo")} icon={Images} label="Upload photo" testid="uploads-add-photo" />
        <Btn onClick={() => setModal("document")} icon={FileText} label="Upload document" testid="uploads-add-document" />
        <Btn onClick={() => setModal("message")} icon={MessageSquare} label="Ask the family admin" testid="uploads-ask-admin" />
      </div>

      {photos.length > 0 && (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1.5">Uploaded photos ({photos.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((p) => (
              <div key={p.id} className="relative rounded-lg overflow-hidden border group" style={{ borderColor: "var(--wofy-border)" }} data-testid={`uploads-photo-${p.id}`}>
                <img src={resolveImage(p.url)} alt={p.caption || ""} className="w-full h-24 object-cover" loading="lazy" />
                {p.url === currentPhoto ? (
                  <span className="absolute bottom-1 left-1 text-[9px] font-semibold rounded-full px-1.5 py-0.5 bg-emerald-600 text-white">Profile</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => { onSetProfile?.(p.url); toast.success("Set as profile photo — click Save to apply."); }}
                    className="absolute inset-x-1 bottom-1 rounded-full text-[10px] font-semibold py-0.5 bg-white/95 border opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`uploads-setprofile-${p.id}`}
                  >
                    <Star className="w-3 h-3 inline -mt-0.5 mr-0.5" /> Set as profile
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {docs.length > 0 && (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-4 mb-1.5">Uploaded documents ({docs.length})</div>
          <ul className="space-y-1.5">
            {docs.map((d) => (
              <li key={d.id}>
                <a
                  href={resolveImage(d.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm rounded-lg border px-3 py-2 hover:shadow-sm"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`uploads-doc-${d.id}`}
                >
                  <FileText className="w-4 h-4 opacity-60 shrink-0" />
                  <span className="truncate flex-1">{d.caption || "Document"}</span>
                  <ExternalLink className="w-3.5 h-3.5 opacity-50" />
                </a>
              </li>
            ))}
          </ul>
        </>
      )}

      {!photos.length && !docs.length && (
        <div className="text-xs opacity-60">
          Nothing uploaded yet — photos and documents you upload here are stored in the family archive and tagged to {memberName || "this member"}.
        </div>
      )}

      {modal && (
        <MemberContributeModal
          kind={modal}
          treeId={treeId}
          memberId={memberId}
          memberName={memberName}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); toast.success("Added to the family archive."); }}
        />
      )}
    </div>
  );
}
